import "./RichText.scss";

import classNames from "classnames";
import { FormInput } from "components/Forms/FormInput";
import { usePasteDialog, useTranslations } from "providers";
import Quill from "quill";
import React, { useEffect, useRef, useState } from "react";

import { logger } from ".";
import { annotateToolbarTooltips } from "./extensions/annotateToolbarTooltips";
import { blockImageDrop } from "./extensions/blockImageDrop";
import { markManualNumbering } from "./extensions/markManualNumbering";
import { setupQuillClipboard } from "./extensions/setupQuillClipboard";
import { getHtml } from "./getHtml";
import { RichTextLinkDialog } from "./RichTextLinkDialog";
import { getRichTextQuillOptions } from "./richTextQuillOptions";
import { RichTextSourceViewer } from "./RichTextSourceViewer";
import { useRichTextLinkDialog } from "./useRichTextLinkDialog";

export interface RichTextProps extends FormInput {
  value: string;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
}

export const RichText: React.FC<RichTextProps> = ({
  name,
  autoFocus,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  className,
  required,
}) => {
  const i18n = useTranslations();

  const editorRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  const [quill, setQuill] = useState<Quill | null>(null);
  const [showSource, setShowSource] = useState(false);
  const { linkDialogProps, setupLinkHandlers } = useRichTextLinkDialog(quillRef, onChangeRef);
  const placeholderToUse = placeholder || i18n.Richtext_Placeholder;

  const { showPasteOptions } = usePasteDialog();
  const showPasteOptionsRef = useRef(showPasteOptions);
  useEffect(() => {
    showPasteOptionsRef.current = showPasteOptions;
  }, [showPasteOptions]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let qr = quillRef.current;
    const er = editorRef.current;
    if (er && !qr) {
      qr = new Quill(er, getRichTextQuillOptions(placeholderToUse));
      quillRef.current = qr;
      setQuill(qr);

      setupQuillClipboard(qr, (req) => showPasteOptionsRef.current(req));
      markManualNumbering(qr);

      qr.on("selection-change", (range) => {
        if (!wrapperRef.current) return;
        if (range !== null) {
          wrapperRef.current.classList.add("ql-focus");
        } else {
          wrapperRef.current.classList.remove("ql-focus");
        }
      });

      setupLinkHandlers(qr);
      // custom clipboard handles paste choices; no DOM paste listener required
      blockImageDrop(er, name);

      const toolbar = qr.container.previousElementSibling as HTMLElement | null;
      if (toolbar) {
        annotateToolbarTooltips(toolbar, [
          { selector: "button.ql-bold", title: i18n.RichText_Tooltip_Bold },
          { selector: "button.ql-italic", title: i18n.RichText_Tooltip_Italic },
          { selector: "button.ql-underline", title: i18n.RichText_Tooltip_Underline },
          { selector: "button.ql-strike", title: i18n.RichText_Tooltip_Strike },
          { selector: 'button.ql-script[value="sub"]', title: i18n.RichText_Tooltip_Script_Sub },
          { selector: 'button.ql-script[value="super"]', title: i18n.RichText_Tooltip_Script_Super },
          { selector: ".ql-header .ql-picker-label", title: i18n.RichText_Tooltip_Heading },
          { selector: ".ql-color .ql-picker-label", title: i18n.RichText_Tooltip_Color },
          { selector: ".ql-background .ql-picker-label", title: i18n.RichText_Tooltip_Background },
          { selector: 'button.ql-list[value="ordered"]', title: i18n.RichText_Tooltip_List_Ordered },
          { selector: 'button.ql-list[value="bullet"]', title: i18n.RichText_Tooltip_List_Bullet },
          { selector: 'button.ql-indent[value="-1"]', title: i18n.RichText_Tooltip_Indent_Minus1 },
          { selector: 'button.ql-indent[value="+1"]', title: i18n.RichText_Tooltip_Indent_Plus1 },
          { selector: "button.ql-blockquote", title: i18n.RichText_Tooltip_Blockquote },
          { selector: "button.ql-link", title: i18n.RichText_Tooltip_Link },
          { selector: "button.ql-clean", title: i18n.RichText_Tooltip_Clean },
          { selector: "button.ql-source", title: i18n.RichText_Tooltip_Source },
        ]);
      }
    }

    if (!qr) return;
    qr.on("text-change", () => {
      const newValue = getHtml(qr);
      logger.trace(`${name}: onChange():`, newValue);
      onChange(newValue);
    });
  }, [onChange, placeholderToUse, required, name, setupLinkHandlers, i18n]);

  useEffect(() => {
    const qr = quillRef.current;
    if (!qr) return;
    if (autoFocus) {
      logger.trace(`${name}: set autofocus`, qr);
      qr.focus();
    }
  }, [quillRef, autoFocus, name]);

  useEffect(() => {
    const qr = quillRef.current;
    if (!qr) return;
    const after = getHtml(qr);

    if (showSource) {
      return;
    }

    if (value !== after) {
      logger.trace(`${name}: prop value changed:`, value);
      const delta = qr.clipboard.convert({ html: value });
      qr.setContents(delta);
    }
  }, [name, showSource, value]);

  className = classNames("ql-container", "ql-snow", className);

  const { key, ...otherProps } = linkDialogProps;

  return (
    <div ref={wrapperRef} className={classNames("sbb-quill", "ql-toolbar-dim", { "ql-source-mode": showSource })}>
      <div
        ref={editorRef}
        className={className}
        onFocus={() => {
          logger.trace(`${name}: onFocus():`);
          return onFocus?.();
        }}
        onBlur={() => {
          logger.trace(`${name}: onBlur():`);
          onBlur?.();
        }}
      />
      <RichTextSourceViewer quill={quill} value={value} onChange={onChange} onModeChange={setShowSource} />
      <RichTextLinkDialog key={key} {...otherProps} />
    </div>
  );
};
